import { useEffect, useRef, useState, useCallback } from 'react';
import { CapacitorSafePoller } from '../utils/capacitorHelper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Polls the unread ride-chat count for a viewer and fires a toast when a new
 * message arrives while the chat panel is closed.
 *
 * @param {string} rideId
 * @param {'rider'|'driver'} viewerRole
 * @param {boolean} chatOpen  when true, polling pauses and count is treated as 0
 * @param {(msg:{title:string,description?:string})=>void} onNewMessage optional toast fn
 */
export default function useRideChatUnread(rideId, viewerRole, chatOpen, onNewMessage) {
  const [unread, setUnread] = useState(0);
  const pollerRef = useRef(null);
  const prevRef = useRef(0);

  const fetchUnread = useCallback(async () => {
    if (!rideId || !viewerRole) return;
    try {
      const res = await fetch(`${API}/chat/unread/${rideId}?user_role=${viewerRole}`);
      const data = await res.json();
      if (!data?.success) return;
      const next = data.unread || 0;
      if (next > prevRef.current && onNewMessage) {
        const otherParty = viewerRole === 'rider' ? 'driver' : 'rider';
        onNewMessage({
          title: 'New message',
          description: `You have a new message from your ${otherParty}.`,
        });
      }
      prevRef.current = next;
      setUnread(next);
    } catch (e) {
      /* silent — retry on next poll */
    }
  }, [rideId, viewerRole, onNewMessage]);

  useEffect(() => {
    if (chatOpen) {
      // chat is open → RideChat marks messages read; reset local count
      prevRef.current = 0;
      setUnread(0);
      return undefined;
    }
    if (!rideId) return undefined;
    fetchUnread();
    pollerRef.current = new CapacitorSafePoller(fetchUnread, 4000);
    pollerRef.current.start();
    return () => pollerRef.current?.stop();
  }, [rideId, chatOpen, fetchUnread]);

  return unread;
}
